import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Check, ChevronRight, Image as ImageIcon, PackagePlus, X,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { useXomicsAuth } from './AuthContext';
import {
  loadCommerceSettings, loadOfficeStock, loadProducts, saveOfficeProduct,
} from './api';
import {
  Empty, Failed, Head, Loading, SearchField, WorkspaceTabs, useLeave,
} from './parts';
import { FilterMenu, isKind, kindOptions } from './ProductFilter';
import { Switch } from './ProductOptions';
import { amountOf, readAmounts, shownAmount } from './storeAmounts';
import { OfficeNamed, StoreOfficeLine, useStoreOffice } from './StoreOffice';
import { StockChange, StockHistory, StockNow } from './StoreStock';
import { NewStockRequest } from './XomicsStockRequests';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './productDetail.css';
import './products.css';

// Selling, Products: what one office does with the products of the catalogue.
// Whether it sells each, at what price, how many it has. Only that office's
// visitors see a change. Nothing is made here: a product is added, named and
// pictured in the catalogue (XomicsProducts.js). What the office asks for
// sending an order has its own page (XomicsShipping.js). How many it has is
// not typed: it is added to or taken from, and every change is kept
// (StoreStock.js).

// A product is on sale in an office's store when it is in the catalogue and
// that office sells it.
const onSale = (product) => product.isActive && product.isSold === true;

function money(amountMinor, currency, minorUnit = 2) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amountMinor / (10 ** minorUnit));
}

const priceIn = (product, currency) => product.prices.find((price) => price.currency === currency && price.isActive) ?? null;
// Only what is shipped is counted: a service or a subscription does not run out.
const keepsStock = (product) => product.shippingWeightGrams !== null;

// `currency`: the one the list reads prices in, said in the column's title:
// that of the country the office is in. Every product has a price in each
// currency, set in its form; a list has room for one.
function StoreList({ products, currency, onOpen }) {
  if (products.length === 0) return <Empty title={tx("ui.products.nothing_matches_17e7aebf")} />;
  return (
    <div className="xo-pc-list is-store">
      <div className="xo-pc-row is-head" aria-hidden="true">
        <span>{tx('ui.store.product')}</span>
        <span>{tx('ui.store.status')}</span>
        <span>{tx('ui.store.price_in', { currency })}</span>
        <span>{tx("ui.products.stock_bcecf456")}</span>
        <span />
      </div>
      {products.map((product) => {
        const selling = onSale(product);
        const price = priceIn(product, currency);
        const stock = product.stock ?? 0;
        const picture = product.mainImageThumbnailUrl ?? product.imageUrl ?? null;
        return (
          <button type="button" key={product.id} className={`xo-pc-row${selling ? ' is-on' : ''}`}
            onClick={() => onOpen(product)}>
            <span className="xo-pc-name">
              <span className="xo-pc-thumb">{picture ? <img src={picture} alt="" loading="lazy" decoding="async" /> : <ImageIcon aria-hidden="true" />}</span>
              <span><strong>{product.name}</strong><small>{product.category?.name ?? ''}</small></span>
            </span>
            <span className="xo-pc-state"><i />{selling ? tx("ui.products.on_sale_8137c8d1") : tx("ui.products.off_sale_01b6ef32")}</span>
            <span className="xo-ps-price">{price ? money(price.amountMinor, currency, price.minorUnit) : <small>{tx('ui.products.no_price')}</small>}</span>
            {!keepsStock(product) ? <span className="xo-pc-number" />
              : stock > 0 ? <span className="xo-pc-number">{stock.toLocaleString()}</span>
                : <span className="xo-pc-number is-empty">{tx('ui.products.options.out_of_stock')}</span>}
            <ChevronRight aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}

// The currencies a form of prices is laid out in: the ones the office's
// visitors pay in first (that of the country it is in before the rest), which
// are always in sight, and the others, which open on request.
function currenciesOf(currencies, office) {
  const active = currencies.filter((currency) => currency.isActive);
  return {
    active,
    needed: active.filter((currency) => office.currencies.includes(currency.code))
      .sort((left, right) => Number(right.code === office.homeCurrency) - Number(left.code === office.homeCurrency)),
    others: active.filter((currency) => !office.currencies.includes(currency.code)),
  };
}

// A price per currency, each in one field.
function PriceFields({
  needed, others, typed, marked, disabled, shown, onShow, onType,
}) {
  const field = (currency) => (
    <label key={currency.code} className={`xo-ps-amount${marked.includes(currency.code) ? ' is-marked' : ''}`} title={currency.name}>
      <b>{currency.code}</b>
      <input type="text" inputMode="decimal" value={typed[currency.code]} disabled={disabled}
        placeholder={(0).toFixed(currency.minorUnit)} aria-label={tx('ui.products.settings.price_in_currency', { currency: currency.code })}
        onChange={(event) => onType(currency.code, event.target.value)} />
    </label>
  );
  return <>
    {needed.length > 0 && <div className="xo-ps-amounts">{needed.map(field)}</div>}
    {others.length > 0 && (shown
      ? <div className="xo-ps-amounts">{others.map(field)}</div>
      : <button type="button" className="xo-link" onClick={onShow}>{tx('ui.store.more_currencies', { count: others.length })}</button>)}
  </>;
}

// One product in one office's store, as a sheet with a tab per job. Prices: on
// sale and its prices, which change on Save (all of it or none). Stock: how
// many the office has, added to or taken from in a form of its own, with the
// history of every change beside it (StoreStock.js), and more asked of a
// warehouse (`onRequest`).
function StoreEditor({
  product, office, currencies, mayManage, onClose, onSaved, onStockChanged, onRequest = null,
}) {
  const { active, needed, others } = useMemo(() => currenciesOf(currencies, office), [currencies, office]);
  // What the office has now: an amount per currency, null where it has none.
  const stored = useMemo(() => Object.fromEntries(active.map((currency) => (
    [currency.code, priceIn(product, currency.code)?.amountMinor ?? null]))), [active, product]);
  const [selling, setSelling] = useState(onSale(product));
  const [prices, setPrices] = useState(() => Object.fromEntries(active.map((currency) => (
    [currency.code, shownAmount(stored[currency.code], currency)]))));
  const [showOthers, setShowOthers] = useState(needed.length === 0);
  // Only what is shipped is counted: a service or a subscription has no Stock tab.
  const stocked = keepsStock(product);
  const [tab, setTab] = useState('prices');
  // The office's stock of it, once read: how many, why it may change, and the
  // page of its history that is asked for now.
  const [stock, setStock] = useState(null);
  // What the history is narrowed to (a kind of change, words, one combination by
  // its SKU), and the older changes read after its first page.
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [only, setOnly] = useState(null);
  const [older, setOlder] = useState({ rows: [], hasMore: false });
  const [reading, setReading] = useState(false);
  // The change to the stock that is being made, in its own form over this one.
  const [change, setChange] = useState(null);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [marked, setMarked] = useState([]);

  useEffect(() => {
    if (!stocked) return undefined;
    let live = true;
    setReading(true);
    loadOfficeStock(office.id, product.id, { kind, search, sku: only })
      .then((answer) => { if (live) { setStock(answer); setOlder({ rows: [], hasMore: false }); } })
      .catch((caught) => { if (live) setError(errorMessage(caught)); })
      .finally(() => { if (live) setReading(false); });
    return () => { live = false; };
  }, [kind, office.id, only, product.id, search, stocked]);

  // The changes before the last one shown.
  async function readOlder() {
    const last = (older.rows.length > 0 ? older.rows : stock.movements).at(-1);
    setReading(true);
    try {
      const page = await loadOfficeStock(office.id, product.id, { kind, search, sku: only, before: last.id });
      setOlder((had) => ({ rows: [...had.rows, ...page.movements], hasMore: page.hasMore }));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setReading(false);
    }
  }

  // While a change to the stock is open over it, this form stays.
  const close = useLeave(busy || change !== null, touched, onClose);

  const picture = product.mainImageThumbnailUrl ?? product.imageUrl ?? null;
  const edit = (change) => (value) => { setTouched(true); change(value); };

  async function save(event) {
    event.preventDefault();
    setError('');
    const changes = {};
    const { changed: priced, wrong } = readAmounts(active, prices, stored);
    if (wrong.length > 0) {
      setMarked(wrong);
      if (wrong.some((code) => others.some((currency) => currency.code === code))) setShowOthers(true);
      setError(tx('ui.store.error.amount', { currencies: wrong.join(', ') }));
      return;
    }
    // On sale needs a price in every currency the office's visitors pay in: said here, before anything is sent.
    const missing = selling ? needed.filter((currency) => amountOf(prices[currency.code], currency.minorUnit) === null).map((currency) => currency.code) : [];
    if (missing.length > 0) {
      setMarked(missing);
      setError(tx('ui.store.error.needs_prices', { currencies: missing.join(', ') }));
      return;
    }
    setMarked([]);
    if (Object.keys(priced).length > 0) changes.prices = priced;
    if (selling !== onSale(product)) changes.isSold = selling;
    if (Object.keys(changes).length === 0) { onClose(); return; }
    setBusy(true);
    try {
      await saveOfficeProduct(office.id, product.id, changes);
      onSaved();
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  const disabled = !mayManage || busy;
  const tabs = [
    { value: 'prices', label: tx('ui.store.prices') },
    ...(stocked ? [{ value: 'stock', label: tx("ui.products.stock_bcecf456"), count: stock?.stock }] : []),
  ];
  // The Prices tab is a form with its one Save; the Stock tab keeps each change at once and only closes.
  const saves = tab === 'prices' && mayManage;

  const pricesForm = `store-product-${product.id}-prices`;

  return (<>
    <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={close}>
      <section className="xo-pm xo-store-sheet" role="dialog" aria-modal="true" aria-labelledby="xo-store-form-title"
        onMouseDown={(event) => event.stopPropagation()}>
        <header className="xo-pm-head">
          <span className="xo-pm-thumb">{picture ? <img src={picture} alt="" /> : <ImageIcon aria-hidden="true" />}</span>
          <div className="xo-pm-title">
            <h2 id="xo-store-form-title">{product.name}</h2>
            <OfficeNamed office={office} />
          </div>
          <div className="xo-pm-head-actions">
            <button type="button" className="xo-icon-btn" aria-label={tx("ui.products.close_bbfa773e")} disabled={busy} onClick={close}>
              <X aria-hidden="true" /></button>
          </div>
        </header>

        <div className="xo-pm-tabbar">
          <WorkspaceTabs className="xo-pm-tabs" value={tab} onChange={setTab} tabs={tabs} label={product.name}
            idPrefix={`store-product-${product.id}-tab`} panelId={`store-product-${product.id}-panel`} />
        </div>

        <div className="xo-pm-body" id={`store-product-${product.id}-panel`} role="tabpanel"
          aria-labelledby={`store-product-${product.id}-tab-${tab}`}>
          {/* Only this tab is a form: nothing typed beside the history can save a price. */}
          <form id={pricesForm} className="xo-pm-panel xo-store-prices" hidden={tab !== 'prices'} onSubmit={save}>
            <section className={`xo-pc-switch-row${selling ? ' is-on' : ''}`}>
              <strong>{selling ? tx("ui.products.on_sale_8137c8d1") : tx("ui.products.off_sale_01b6ef32")}</strong>
              <Switch on={selling} disabled={disabled} label={tx("ui.products.on_sale_8137c8d1")} onChange={edit(setSelling)} />
            </section>
            <section className="xo-pm-card">
              <h3>{tx('ui.store.prices')}</h3>
              <PriceFields needed={needed} others={others} typed={prices} marked={marked} disabled={disabled}
                shown={showOthers} onShow={() => setShowOthers(true)}
                onType={(code, value) => edit(setPrices)((current) => ({ ...current, [code]: value }))} />
            </section>
          </form>

          {stocked && (
            <div className={`xo-pm-panel xo-store-stock${stock?.stockPerCombination ? ' is-entries' : ''}`} hidden={tab !== 'stock'}>
              {stock === null ? <p className="xo-stock-none">{tx('ui.store.stock.reading')}</p> : <>
                <StockNow stock={stock} only={only} onOnly={setOnly} onChange={disabled ? undefined : setChange}
                  onRequest={onRequest} />
                <StockHistory stock={stock} older={older.rows} hasMore={older.rows.length > 0 ? older.hasMore : stock.hasMore}
                  kind={kind} search={search} only={only} busy={reading} requestPath="/xomics/store/stock-requests"
                  onKind={setKind} onSearch={setSearch} onOnly={setOnly} onMore={readOlder} />
              </>}
            </div>
          )}
        </div>

        <footer className="xo-pc-form-foot">
          {error ? <p className="xo-alert t-bad" role="alert">{error}</p> : <span />}
          <button type="button" className="xo-btn-2" disabled={busy} onClick={close}>
            {saves ? tx('ui.common.cancel') : tx("ui.products.close_bbfa773e")}</button>
          {saves && <button type="submit" form={pricesForm} className="xo-btn" disabled={busy}>
            {busy ? tx('ui.products.settings.saving') : tx('ui.products.settings.save')}</button>}
        </footer>
      </section>
    </div>
    {change !== null && stock !== null && (
      <StockChange office={office} product={product} stock={stock} change={change}
        onClose={() => setChange(null)}
        onDone={(after) => {
          // The answer is the whole history's first page: what narrowed it is let go.
          setKind(''); setSearch(''); setOnly(null); setOlder({ rows: [], hasMore: false });
          setStock(after); setChange(null); onStockChanged();
        }} />
    )}
  </>);
}

export default function XomicsStoreProducts() {
  const { permissions } = useXomicsAuth();
  const mayManage = permissions.includes('PRODUCT_MANAGE');
  const mayAsk = permissions.includes('STOCK_REQUEST_MANAGE');
  // A request to a warehouse being made, from the page ({}) or from one product ({ productId }).
  const [asking, setAsking] = useState(null);
  const store = useStoreOffice();
  const office = store.office;
  const officeId = office?.id ?? null;
  const load = useCallback(() => loadProducts(officeId), [officeId]);
  const list = useLiveResource(['products'], load, { enabled: officeId !== null });
  const settings = useLiveResource(['products'], useCallback(() => loadCommerceSettings(), []));
  // Every product, the ones on sale, or the ones off sale.
  const [filter, setFilter] = useState('all');
  // Every kind of product, or one of them.
  const [kind, setKind] = useState('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null);
  const [notice, setNotice] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  // Arriving for one product (the catalogue sends here the one just added):
  // its form opens, once.
  useEffect(() => {
    const wanted = searchParams.get('product');
    if (wanted === null || list.data === null) return;
    if (list.data.some((product) => product.id === Number(wanted))) setOpenId(Number(wanted));
    const rest = new URLSearchParams(searchParams);
    rest.delete('product');
    setSearchParams(rest, { replace: true });
  }, [list.data, searchParams, setSearchParams]);

  const products = useMemo(() => list.data ?? [], [list.data]);
  const inState = useCallback((product) => filter === 'all' || (filter === 'on') === onSale(product), [filter]);
  // Each filter counts what the other leaves: the kinds among the chosen
  // state, the states among the chosen kind.
  const ofKind = useMemo(() => products.filter((product) => isKind(product, kind)), [kind, products]);
  const selling = useMemo(() => ofKind.filter(onSale).length, [ofKind]);
  const kinds = useMemo(() => kindOptions(products.filter(inState)), [inState, products]);
  // What is on sale first, then by name.
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return ofKind
      .filter((product) => needle === '' || `${product.name} ${product.category?.name ?? ''} ${product.code}`.toLowerCase().includes(needle))
      .filter(inState)
      .sort((left, right) => Number(onSale(right)) - Number(onSale(left)) || left.name.localeCompare(right.name));
  }, [inState, ofKind, query]);
  const open = products.find((product) => product.id === openId) ?? null;

  const head = (
    <Head title={tx('ui.nav.products_fe0a091f')} under={<StoreOfficeLine store={store} />}>
      {mayAsk && office !== null && (
        <button type="button" className="xo-btn-2" onClick={() => { setNotice(''); setAsking({}); }}>
          <PackagePlus aria-hidden="true" />{tx('ui.stock_requests.request_stock')}
        </button>
      )}
    </Head>
  );

  if (store.error) return <Failed message={store.error} onRetry={store.refresh} />;
  if (store.loading) return <Loading label={tx("ui.products.reading_the_catalogue_2a05727e")} />;
  // An account whose location keeps no store has nothing to set here.
  if (office === null) return <div className="xo-products-page xo-pc-page">{head}<Empty title={tx('ui.store.no_office')} /></div>;
  if (list.error) return <Failed message={list.error} onRetry={list.refresh} />;
  if (settings.error) return <Failed message={settings.error} onRetry={settings.refresh} />;
  if (list.data === null || settings.data === null) return <Loading label={tx("ui.products.reading_the_catalogue_2a05727e")} />;

  return (
    <div className="xo-products-page xo-pc-page">
      {head}
      {notice && <p className="xo-alert t-ok" role="status"><Check aria-hidden="true" />{notice}</p>}
      <div className="xo-product-navigation">
        <div className="xo-product-toolbar">
          <SearchField className="xo-product-search" value={query} onChange={setQuery} placeholder={tx("ui.products.search_products_4412bfe5")} />
          <div className="xo-product-toolbar-actions">
            <FilterMenu value={kind} onChange={setKind} options={kinds} label={tx('ui.store.filter')} />
          </div>
        </div>
        <div className="xo-product-section-row">
          <WorkspaceTabs className="xo-product-sections" value={filter} onChange={setFilter} label={tx('ui.store.status')}
            tabs={[
              { value: 'all', label: tx('ui.store.all'), count: ofKind.length },
              { value: 'on', label: tx("ui.products.on_sale_8137c8d1"), count: selling },
              { value: 'off', label: tx("ui.products.off_sale_01b6ef32"), count: ofKind.length - selling },
            ]} />
        </div>
      </div>
      {/* Another office was chosen and its store is being read: what is shown is still the last one's. */}
      <div className={list.loading ? 'xo-store-turning' : undefined}>
        <StoreList products={shown} currency={office.homeCurrency} onOpen={(product) => { setNotice(''); setOpenId(product.id); }} />
      </div>
      {open !== null && <StoreEditor key={`${officeId}-${open.id}`} product={open} office={office}
        currencies={settings.data.currencies} mayManage={mayManage} onClose={() => setOpenId(null)}
        onSaved={() => { setOpenId(null); setNotice(tx('ui.store.saved', { name: open.name })); list.refresh(); }}
        onStockChanged={() => list.refresh()}
        onRequest={mayAsk ? () => { setOpenId(null); setAsking({ productId: open.id }); } : null} />}
      {asking !== null && <NewStockRequest office={office} preset={asking.productId ? asking : null}
        onClose={() => setAsking(null)}
        onSent={(made) => {
          setAsking(null);
          setNotice(tx('ui.stock_requests.sent', { number: made.number, warehouse: made.warehouse.name }));
        }} />}
    </div>
  );
}
